import { CrossingGame, BallDirection } from './CrossingGame.js';
import { Grid } from '../../core/Grid.js';
import { Position, Direction, oppositeDir, positionKey } from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface CrossingRenderConfig extends Partial<RenderConfig> {
    teleporterColor?: string;
    pairLineColor?: string;
    pendingColor?: string;
    validHighlightColor?: string;
    correctBadgeColor?: string;
    incorrectBadgeColor?: string;
    pathWidth?: number;
}

const CROSSING_DEFAULTS = {
    teleporterColor: COLORS.teal,
    pairLineColor: colorWithAlpha('teal', 0.5),
    pendingColor: colorWithAlpha('teal', 0.6),
    validHighlightColor: colorWithAlpha('teal', 0.3),
    correctBadgeColor: COLORS.teal,
    incorrectBadgeColor: COLORS.amber,
    pathWidth: 3
};

// ── Teleporter animation (docs/juice.md) ──────────────────────────────
//
// A pair is one object, not two pieces that happen to match: two triangles
// aiming at each other along the line that links them. So it animates as one —
// the link draws itself between the cells and each triangle unfolds along its own
// pointing direction, from its base out to its tip, which is the direction it
// throws a ball.
//
// It draws *out of the cell clicked first*. That is the player's own gesture:
// they pick one end, then the other, and the pair grows the way they built it.
// The pending circle is the only record of which came first, so the renderer
// keeps hold of it as it goes.

interface PairTween {
    kind: 'enter' | 'exit';
    start: number;
    /** The two cells, ordered so `from` is the one the pair grew out of. */
    from: Position;
    to: Position;
}

/** A pair's canonical key, independent of which end is named first. */
function pairKey(a: Position, b: Position): string {
    const ka = positionKey(a), kb = positionKey(b);
    return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

export class CrossingRenderer extends GridRenderer {
    private crossingConfig: typeof CROSSING_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private pairTweens = new Map<string, PairTween>();
    private pairState = new Map<string, [Position, Position]>();  // what the last frame drew
    private pairsSynced: boolean = false;                         // first frame seeds
    /** The cell a placement is pending on — the half-made pair, and the record of
     *  which end of a finished pair was clicked first. */
    private pending: Position | null = null;
    private pendingEnter: number | null = null;
    private pendingExit: { pos: Position; start: number } | null = null;
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: CrossingGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: CrossingRenderConfig = {}) {
        super(canvas, grid, config);
        this.crossingConfig = { ...CROSSING_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every teleporter at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.pairTweens.clear();
        this.pendingEnter = null;
        this.pendingExit = null;
    }

    render(game: CrossingGame): void {
        const grid = game.getGrid();

        this.lastGame = game;
        this.animating = false;
        // Pairs first: the diff reads the pending cell as it stood *before* this
        // frame, which is the only record of which end was clicked first.
        this.syncPairTweens(game.getTeleporterPairs());
        this.syncPendingTween(game.getPendingTeleporter());

        this.clear();
        this.drawGrid();

        // Draw in order:
        // 1. Gate mode highlights (when vortex gate mode active)
        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        // 2. Valid position highlights (when pending)
        this.drawValidHighlights(game);

        // 3. Dotted lines between teleporter pairs
        this.drawPairLines(game);

        // 4. Ball paths (if digit selected)
        this.drawBallPaths(game);

        // 5. Teleporters
        this.drawTeleporters(game);

        // 6. Pending teleporter
        this.drawPendingTeleporter(game);

        // 7. Active vortex gates
        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
        }

        // 8. Digit backgrounds
        this.drawDigitBackgrounds(grid, game);

        // 9. Digits
        this.drawDigits(grid);

        // 10. Badges (two per digit)
        this.drawBadges(game);

        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Teleporter animation (docs/juice.md) ──────────────────────────

    /**
     * Diff the pairs against the last frame. A new pair grows out of the cell the
     * player clicked first — the one that was pending until this frame — so the
     * motion follows the order they built it in. A removed pair retracts into the
     * end it was drawn from, and a bulk change fans out in reading order.
     */
    private syncPairTweens(pairs: Array<[Position, Position]>): void {
        const next = new Map<string, [Position, Position]>();
        for (const [a, b] of pairs) next.set(pairKey(a, b), [a, b]);

        if (!this.tweens.isEnabled()) {
            this.pairTweens.clear();
            this.pairState = next;
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a saved
        // solution would lay every pair again.
        if (!this.pairsSynced) {
            this.pairState = next;
            this.pairsSynced = true;
            return;
        }

        const changed: string[] = [];
        for (const key of next.keys()) if (!this.pairState.has(key)) changed.push(key);
        for (const key of this.pairState.keys()) if (!next.has(key)) changed.push(key);
        if (changed.length === 0) return;

        const rank = (key: string) => {
            const [row, col] = key.split('|')[0].split(',').map(Number);
            return row * this.grid.size + col;
        };
        changed.sort((a, b) => rank(a) - rank(b));

        const pendingKey = this.pending ? positionKey(this.pending) : null;
        const now = this.tweens.now();
        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            const entering = next.has(key);
            const ends = (entering ? next.get(key) : this.pairState.get(key))!;
            // Grow out of the end that was pending — the one clicked first. Off a
            // click (a restore, a reset) either end will do, so take it as stored;
            // for a removal, keep whichever end it grew from.
            const grewFrom = this.pairTweens.get(key)?.from;
            const first = pendingKey === positionKey(ends[1]) ? ends[1]
                : grewFrom && !entering && positionKey(grewFrom) === positionKey(ends[1]) ? ends[1]
                : ends[0];
            const other = positionKey(first) === positionKey(ends[0]) ? ends[1] : ends[0];
            this.pairTweens.set(key, { kind: entering ? 'enter' : 'exit', start, from: first, to: other });
        });

        this.pairState = next;
    }

    /** How far a pair is drawn this frame. */
    private pairExtent(tween: PairTween, now: number): { grown: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        // The `exit` curve runs the *disappearance*, so invert it.
        return { grown: tween.kind === 'enter' ? value : 1 - value, done };
    }

    /**
     * The pending marker appears when a placement starts and goes when it ends —
     * completed into a pair, or cancelled. Either way it is the same motion, so the
     * circle collapses as the pair it became unfolds in the same cell.
     */
    private syncPendingTween(pending: Position | null): void {
        const same = pending && this.pending &&
            pending.row === this.pending.row && pending.col === this.pending.col;
        if (same) return;

        if (!this.tweens.isEnabled()) {
            this.pendingEnter = null;
            this.pendingExit = null;
        } else {
            const now = this.tweens.now();
            this.pendingExit = this.pending ? { pos: this.pending, start: now } : null;
            this.pendingEnter = pending ? now : null;
        }
        this.pending = pending ? { ...pending } : null;
    }

    private drawValidHighlights(game: CrossingGame): void {
        const pending = game.getPendingTeleporter();
        if (!pending) return;

        const validPositions = game.getValidPairPositions(pending);
        const { validHighlightColor } = this.crossingConfig;

        for (const pos of validPositions) {
            this.highlightCell(pos, validHighlightColor);
        }
    }

    private drawPairLines(game: CrossingGame): void {
        const { pairLineColor } = this.crossingConfig;
        const now = this.tweens.now();

        this.ctx.strokeStyle = pairLineColor;
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([5, 5]);

        // Every pair the board holds, plus the ones on their way out — those are
        // gone from game state and live only in the tween.
        for (const { ends, grown } of this.pairFrames(game, now)) {
            const from = this.getCellCenter(ends[0]);
            const to = this.getCellCenter(ends[1]);
            this.ctx.beginPath();
            this.ctx.moveTo(from.x, from.y);
            this.ctx.lineTo(from.x + (to.x - from.x) * grown, from.y + (to.y - from.y) * grown);
            this.ctx.stroke();
        }

        this.ctx.setLineDash([]);
    }

    /**
     * Every pair to draw this frame, each as its two cells — ordered so the first is
     * the end it grows out of — and how far along it is. Pairs at rest come back at
     * `grown: 1`; ones being removed are already out of game state, so they come
     * from the tween alone.
     */
    private pairFrames(
        game: CrossingGame, now: number
    ): Array<{ ends: [Position, Position]; grown: number }> {
        const frames: Array<{ ends: [Position, Position]; grown: number }> = [];
        const live = new Set<string>();

        for (const [a, b] of game.getTeleporterPairs()) {
            const key = pairKey(a, b);
            live.add(key);
            const tween = this.pairTweens.get(key);
            if (!tween) {
                frames.push({ ends: [a, b], grown: 1 });
                continue;
            }
            const { grown, done } = this.pairExtent(tween, now);
            if (done) {
                this.pairTweens.delete(key);
                frames.push({ ends: [a, b], grown: 1 });
            } else {
                this.animating = true;
                frames.push({ ends: [tween.from, tween.to], grown });
            }
        }

        for (const [key, tween] of this.pairTweens) {
            if (tween.kind !== 'exit' || live.has(key)) continue;
            const { grown, done } = this.pairExtent(tween, now);
            if (done) {
                this.pairTweens.delete(key);
                continue;
            }
            this.animating = true;
            frames.push({ ends: [tween.from, tween.to], grown });
        }

        return frames;
    }

    private drawBallPaths(game: CrossingGame): void {
        const selectedDigit = game.getSelectedDigit();
        if (selectedDigit === null) return;

        const grid = game.getGrid();
        const startPos = grid.getDigitPosition(selectedDigit);
        if (!startPos) return;

        const { correctBadgeColor, incorrectBadgeColor } = this.crossingConfig;
        const downColor = game.isCorrectDown(selectedDigit) ? correctBadgeColor : incorrectBadgeColor;
        const rightColor = game.isCorrectRight(selectedDigit) ? correctBadgeColor : incorrectBadgeColor;

        this.drawPath(game, startPos, 'down', downColor);
        this.drawPath(game, startPos, 'right', rightColor);
    }

    private drawPath(game: CrossingGame, startPos: Position, direction: BallDirection, color: string): void {
        const path = game.simulateBallPath(startPos, direction);
        const { pathWidth } = this.crossingConfig;

        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = pathWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        if (path.positions.length < 2) return;

        this.ctx.beginPath();
        let needsMoveTo = true;

        for (let i = 0; i < path.positions.length; i++) {
            const pos = path.positions[i];
            const center = this.getCellCenter(pos);

            // Check if this is a teleport jump (prev and current are paired teleporters)
            if (i > 0) {
                const prev = path.positions[i - 1];

                // Check if we just teleported: prev is entry, current is its paired exit
                const pairedWithPrev = game.getPairedTeleporter(prev);
                const isTeleportJump = pairedWithPrev !== null &&
                    pairedWithPrev.row === pos.row &&
                    pairedWithPrev.col === pos.col;

                if (isTeleportJump) {
                    // Teleport jump - end current segment and start new
                    this.ctx.stroke();
                    this.ctx.beginPath();
                    needsMoveTo = true;
                }
            }

            if (needsMoveTo) {
                this.ctx.moveTo(center.x, center.y);
                needsMoveTo = false;
            } else {
                // Handle wrapping - draw to edge, then from opposite edge
                const prev = path.positions[i - 1];
                const { cellSize, padding } = this.config;
                const gridEnd = padding + this.grid.size * cellSize;

                if (direction === 'down' && pos.row === 0 && prev.row === this.grid.size - 1) {
                    // Wrapped from bottom to top
                    const prevCenter = this.getCellCenter(prev);
                    this.ctx.lineTo(prevCenter.x, gridEnd);
                    this.ctx.stroke();
                    this.ctx.beginPath();
                    this.ctx.moveTo(center.x, padding);
                    this.ctx.lineTo(center.x, center.y);
                } else if (direction === 'right' && pos.col === 0 && prev.col === this.grid.size - 1) {
                    // Wrapped from right to left
                    const prevCenter = this.getCellCenter(prev);
                    this.ctx.lineTo(gridEnd, prevCenter.y);
                    this.ctx.stroke();
                    this.ctx.beginPath();
                    this.ctx.moveTo(padding, center.y);
                    this.ctx.lineTo(center.x, center.y);
                } else {
                    this.ctx.lineTo(center.x, center.y);
                }
            }
        }

        this.ctx.stroke();

        // Draw edge markers (triangles at cell boundaries, not cell centers)
        if (path.positions.length >= 2) {
            const { cellSize, padding } = this.config;

            this.ctx.fillStyle = color;

            // End arrow: at the entry edge of the destination cell
            const lastPos = path.positions[path.positions.length - 1];
            this.ctx.beginPath();
            if (direction === 'down') {
                // Arrow at north edge of destination, pointing down
                const edgeX = this.getCellCenter(lastPos).x;
                const edgeY = padding + lastPos.row * cellSize;
                this.ctx.moveTo(edgeX - 5, edgeY - 8);
                this.ctx.lineTo(edgeX + 5, edgeY - 8);
                this.ctx.lineTo(edgeX, edgeY);
            } else {
                // Arrow at west edge of destination, pointing right
                const edgeX = padding + lastPos.col * cellSize;
                const edgeY = this.getCellCenter(lastPos).y;
                this.ctx.moveTo(edgeX - 8, edgeY - 5);
                this.ctx.lineTo(edgeX - 8, edgeY + 5);
                this.ctx.lineTo(edgeX, edgeY);
            }
            this.ctx.closePath();
            this.ctx.fill();

            // First edge marker: triangle on the first edge crossed after leaving the digit
            let edgeCount = 0;
            for (let i = 1; i < path.positions.length; i++) {
                const prev = path.positions[i - 1];
                const curr = path.positions[i];

                // Skip teleport jumps
                const pairedWithPrev = game.getPairedTeleporter(prev);
                if (pairedWithPrev !== null &&
                    pairedWithPrev.row === curr.row &&
                    pairedWithPrev.col === curr.col) {
                    continue;
                }

                // Skip the first edge (exiting the digit cell)
                if (edgeCount++ === 0) continue;

                this.ctx.beginPath();
                if (direction === 'down') {
                    const edgeX = this.getCellCenter(prev).x;
                    const edgeY = padding + (prev.row + 1) * cellSize;
                    this.ctx.moveTo(edgeX - 5, edgeY - 8);
                    this.ctx.lineTo(edgeX + 5, edgeY - 8);
                    this.ctx.lineTo(edgeX, edgeY);
                } else {
                    const edgeX = padding + (prev.col + 1) * cellSize;
                    const edgeY = this.getCellCenter(prev).y;
                    this.ctx.moveTo(edgeX - 8, edgeY - 5);
                    this.ctx.lineTo(edgeX - 8, edgeY + 5);
                    this.ctx.lineTo(edgeX, edgeY);
                }
                this.ctx.closePath();
                this.ctx.fill();
                break;
            }
        }
    }

    private drawTeleporters(game: CrossingGame, colorOverride?: string): void {
        const color = colorOverride ?? this.crossingConfig.teleporterColor;
        const { cellSize } = this.config;

        // The scoring overlay redraws the gates in a flat colour on a frozen board:
        // it reads state, it doesn't advance it. (The diff already ran, in render.)
        if (colorOverride !== undefined) {
            for (const [a, b] of game.getTeleporterPairs()) {
                const direction = this.getTeleporterDirection(a, b);
                this.drawTeleporterTriangle(a, direction, color, cellSize);
                this.drawTeleporterTriangle(b, oppositeDir(direction), color, cellSize);
            }
            return;
        }

        for (const { ends, grown } of this.pairFrames(game, this.tweens.now())) {
            const [a, b] = ends;
            const direction = this.getTeleporterDirection(a, b);
            this.drawTeleporterTriangle(a, direction, color, cellSize, grown);
            this.drawTeleporterTriangle(b, oppositeDir(direction), color, cellSize, grown);
        }
    }

    /** Scoring overlay: redraw the teleporter gates (primary) in amber. */
    protected drawScoringPrimary(game: CrossingGame): void {
        this.drawTeleporters(game, COLORS.amber);
    }

    private getTeleporterDirection(from: Position, to: Position): Direction {
        if (from.row === to.row) {
            return from.col < to.col ? 'E' : 'W';
        } else {
            return from.row < to.row ? 'S' : 'N';
        }
    }

    /**
     * A teleporter: a triangle filling half its cell, aimed at its pair. `grown`
     * pulls the tip back toward the base, so it unfolds along the direction it
     * throws a ball — and at 0 it is a degenerate line with nothing to fill.
     */
    private drawTeleporterTriangle(
        pos: Position,
        direction: Direction,
        color: string,
        cellSize: number,
        grown: number = 1
    ): void {
        if (grown <= 0) return;
        const topLeft = this.getCellTopLeft(pos);
        const margin = 12;
        const x = topLeft.x + margin;
        const y = topLeft.y + margin;
        const size = cellSize - margin * 2;
        const reach = size * grown;

        this.ctx.fillStyle = color;
        this.ctx.beginPath();

        switch (direction) {
            case 'E':
                // Triangle pointing right (occupies left half)
                this.ctx.moveTo(x, y);
                this.ctx.lineTo(x, y + size);
                this.ctx.lineTo(x + reach, y + size / 2);
                break;
            case 'W':
                // Triangle pointing left (occupies right half)
                this.ctx.moveTo(x + size, y);
                this.ctx.lineTo(x + size, y + size);
                this.ctx.lineTo(x + size - reach, y + size / 2);
                break;
            case 'S':
                // Triangle pointing down (occupies top half)
                this.ctx.moveTo(x, y);
                this.ctx.lineTo(x + size, y);
                this.ctx.lineTo(x + size / 2, y + reach);
                break;
            case 'N':
                // Triangle pointing up (occupies bottom half)
                this.ctx.moveTo(x, y + size);
                this.ctx.lineTo(x + size, y + size);
                this.ctx.lineTo(x + size / 2, y + size - reach);
                break;
        }

        this.ctx.closePath();
        this.ctx.fill();
    }

    private drawPendingTeleporter(game: CrossingGame): void {
        const now = this.tweens.now();

        // The one on its way out — completed into a pair, or cancelled. It is gone
        // from game state, so it lives only in the tween.
        if (this.pendingExit) {
            const { value, done } = this.tweens.tween(this.pendingExit.start, MOTION.exit, now);
            if (done) this.pendingExit = null;
            else {
                this.animating = true;
                this.drawPendingMarker(this.pendingExit.pos, 1 - value);
            }
        }

        const pending = game.getPendingTeleporter();
        if (!pending) return;

        let scale = 1;
        if (this.pendingEnter !== null) {
            const { value, done } = this.tweens.tween(this.pendingEnter, MOTION.enter, now);
            if (done) this.pendingEnter = null;
            else {
                this.animating = true;
                scale = value;
            }
        }
        this.drawPendingMarker(pending, scale);
    }

    /** The half-made pair's marker, at `scale` of its full size. */
    private drawPendingMarker(pos: Position, scale: number): void {
        if (scale <= 0.01) return;
        const center = this.getCellCenter(pos);
        const { pendingColor } = this.crossingConfig;
        const radius = 12 * scale;

        this.ctx.fillStyle = pendingColor;
        this.ctx.beginPath();
        this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
        this.ctx.fill();

        // Add a border
        this.ctx.strokeStyle = this.crossingConfig.teleporterColor;
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
    }

    private drawDigitBackgrounds(grid: ReturnType<CrossingGame['getGrid']>, game: CrossingGame): void {
        const { cellSize, backgroundColor } = this.config;
        const { teleporterColor } = this.crossingConfig;
        const positions = grid.getAllDigitPositions();
        const correctDigits = new Set(game.getCorrectDigits());

        for (const [digit, pos] of positions) {
            const topLeft = this.getCellTopLeft(pos);
            const isCorrect = correctDigits.has(digit);

            // Fill with background color
            this.ctx.fillStyle = backgroundColor;
            this.ctx.fillRect(topLeft.x + 4, topLeft.y + 4, cellSize - 8, cellSize - 8);

            // Border: teal if fully correct, gray otherwise
            const borderColor = isCorrect ? teleporterColor : COLORS.borderGray;
            this.drawCellBorder(pos, borderColor, 4);
        }
    }

    private drawBadges(game: CrossingGame): void {
        const grid = game.getGrid();
        const { correctBadgeColor, incorrectBadgeColor } = this.crossingConfig;

        for (let digit = this.grid.minDigit; digit <= this.grid.maxDigit; digit++) {
            const pos = grid.getDigitPosition(digit);
            if (!pos) continue;

            // Down ball badge (bottom of cell)
            const nextDown = game.getNextDigitForBall(digit, 'down');
            const isCorrectDown = game.isCorrectDown(digit);
            const downLabel = nextDown !== null ? nextDown.toString() : '?';
            this.drawBadge(pos, 'S', downLabel, isCorrectDown ? correctBadgeColor : incorrectBadgeColor, 'sm', true);

            // Right ball badge (right side of cell)
            const nextRight = game.getNextDigitForBall(digit, 'right');
            const isCorrectRight = game.isCorrectRight(digit);
            const rightLabel = nextRight !== null ? nextRight.toString() : '?';
            this.drawBadge(pos, 'E', rightLabel, isCorrectRight ? correctBadgeColor : incorrectBadgeColor, 'sm', true);
        }
    }
}
